import { setUser } from '@sentry/react'

import { epicRequest } from 'app'
import { trackEvent } from 'global-utils/Analytics'
import storage from 'global-utils/Storage'
import System from 'system'

import VerifyPasswordResetCode from 'components/ui/VerifyPasswordResetCode'

import store from '../../../config/store'
import {
  SYSTEM_MESSAGE_ACTION,
  SYSTEM_MESSAGE_INITIAL_STATE,
  systemMessageCanceled,
  systemMessageShow,
} from '../SystemMessageActions'

export const URL_OAUTH_API = `${System.env.api.host}/oauth`

export const AUTH_ACTION = 'AUTH_ACTION'

export const AUTH_FETCH = 'AUTH_FETCH'
export const AUTH_FETCH_CANCELED = 'AUTH_FETCH_CANCELED'
export const AUTH_FETCH_ERROR = 'AUTH_FETCH_ERROR'
export const AUTH_FETCH_FULFILLED = 'AUTH_FETCH_FULFILLED'

export const AUTH_REFRESH_TOKEN = 'AUTH_REFRESH_TOKEN'
export const AUTH_REFRESH_TOKEN_ERROR = 'AUTH_REFRESH_TOKEN_ERROR'
export const AUTH_REFRESH_TOKEN_FULFILLED = 'AUTH_REFRESH_TOKEN_FULFILLED'
export const AUTH_REFRESH_TOKEN_CANCELED = 'AUTH_REFRESH_TOKEN_CANCELED'

export const AUTH_RESET_PASSWORD = 'AUTH_RESET_PASSWORD'
export const AUTH_RESET_PASSWORD_ERROR = 'AUTH_RESET_PASSWORD_ERROR'
export const AUTH_RESET_PASSWORD_FULFILLED = 'AUTH_RESET_PASSWORD_FULFILLED'
export const AUTH_RESET_PASSWORD_CANCELED = 'AUTH_RESET_PASSWORD_CANCELED'

export const AUTH_LOGIN_CANCELED = 'AUTH_LOGIN_CANCELED'

export const AUTH_LOGIN_CREDENTIALS = 'AUTH_LOGIN_CREDENTIALS'
export const AUTH_LOGIN_CREDENTIALS_ERROR = 'AUTH_LOGIN_CREDENTIALS_ERROR'
export const AUTH_LOGIN_CREDENTIALS_FULFILLED = 'AUTH_LOGIN_CREDENTIALS_FULFILLED'

export const AUTH_LOGIN_NETWORK = 'AUTH_LOGIN_NETWORK'
export const AUTH_LOGIN_NETWORK_ERROR = 'AUTH_LOGIN_NETWORK_ERROR'
export const AUTH_LOGIN_NETWORK_FULFILLED = 'AUTH_LOGIN_NETWORK_FULFILLED'

export const AUTH_LOGOUT = 'AUTH_LOGOUT'
export const AUTH_LOGOUT_ERROR = 'AUTH_LOGOUT_ERROR'
export const AUTH_LOGOUT_FULFILLED = 'AUTH_LOGOUT_FULFILLED'

export const AUTH_CHANGE_PASSWORD = 'AUTH_CHANGE_PASSWORD'
export const AUTH_CHANGE_PASSWORD_ERROR = 'AUTH_CHANGE_PASSWORD_ERROR'
export const AUTH_CHANGE_PASSWORD_FULFILLED = 'AUTH_CHANGE_PASSWORD_FULFILLED'
export const AUTH_CHANGE_PASSWORD_CANCELED = 'AUTH_CHANGE_PASSWORD_CANCELED'

export const AUTH_SET_AVATAR = 'AUTH_SET_AVATAR'

export const TOURNAMENT_LAST_VIEWED = 'TOURNAMENT_LAST_VIEWED'

export const AUTH_INITIAL_STATE = {
  action: null,
  error: null,
  loaded: false,
  loading: false,
  provider: null,
  usr: null,
}
export const AUTH_HEADERS = {}
export const AUTH_USER = {}

export const authAction = (action) => ({
  payload: action,
  type: AUTH_ACTION,
})

export const authSetAvatar = (avatar) => {
  AUTH_USER.avatar = avatar
  storage.set('swa.user', AUTH_USER, storage.getFlashed('rememberAccess') || true)
  return {
    payload: avatar,
    type: AUTH_SET_AVATAR,
  }
}

export const tournamentLastViewed = (id) => {
  storage.save('swv.last.tournament', id)
  return {
    payload: id,
    type: TOURNAMENT_LAST_VIEWED,
  }
}

export const authLoginByCredentials = (username, password, rememberAccess = false) => {
  if (rememberAccess) {
    storage.flash('rememberAccess', true)
  }

  return {
    payload: {
      client_id: System.env.client.id,
      client_secret: System.env.client.secret,
      grant_type: 'password',
      password,
      provider: 'SweatBits',
      scope: 'user',
      username,
    },
    type: AUTH_LOGIN_CREDENTIALS,
  }
}

export const authLoginByNetwork = (network, accessToken, rememberAccess = false) => {
  if (rememberAccess) {
    storage.flash('rememberAccess', true)
  }

  return {
    payload: {
      access_token: accessToken,
      client_id: System.env.client.id,
      client_secret: System.env.client.secret,
      grant_type: 'social',
      network,
      provider: 'SweatBits',
      scope: 'user',
    },
    type: AUTH_LOGIN_NETWORK,
  }
}

export const authLoginFulfilled = (payload) => {
  storage.set('swa.type', 'swa.credentials', storage.getFlashed('rememberAccess') || true)
  storage.set(
    'swa.credentials',
    {
      at: payload.access_token,
      ex: payload.expires_in * 1000 + Date.now(),
      rt: payload.refresh_token,
      tt: payload.token_type,
    },
    storage.getFlashed('rememberAccess') || true,
  )

  trackEvent('Authentication', 'Login', 'Done')

  // force reload
  if (document.location.pathname !== '/') {
    if (document.location.pathname.indexOf('/auth/') >= 0) {
      document.location.href = '/'
    } else {
      document.location = document.location.href
    }
  }

  return {
    payload,
    type: AUTH_LOGIN_CREDENTIALS_FULFILLED,
  }
}

export const authLoginCanceled = () => ({
  payload: null,
  type: AUTH_LOGIN_CANCELED,
})

export const authLoginError = (payload) => ({
  payload,
  type: AUTH_LOGIN_CREDENTIALS_ERROR,
})

export const authRefreshToken = (token) => ({
  payload: {
    client_id: System.env.client.id,
    client_secret: System.env.client.secret,
    grant_type: 'refresh_token',
    provider: 'SweatBits',
    refresh_token: token,
    scope: 'user',
  },
  type: AUTH_REFRESH_TOKEN,
})

export const authRefreshTokenFulfilled = (payload) => {
  authLoginFulfilled(payload)
  return {
    payload,
    type: AUTH_REFRESH_TOKEN_FULFILLED,
  }
}

export const authRefreshTokenCanceled = () => ({
  payload: null,
  type: AUTH_REFRESH_TOKEN_CANCELED,
})

export const authRefreshTokenError = ({ payload }) => {
  storage.clear()
  return {
    payload,
    type: AUTH_REFRESH_TOKEN_ERROR,
  }
}

export const authFetch = () => ({
  payload: null,
  type: AUTH_FETCH,
})

export const authFetchFulfilled = (payload) => {
  ;['id', 'username', 'name', 'first_name', 'last_name', 'email', 'avatar', 'level', 'points'].forEach((key) => {
    AUTH_USER[key] = payload[key]
  })

  storage.set('swa.user', AUTH_USER, storage.getFlashed('rememberAccess') || true)

  if (process.env.NODE_ENV === 'production') {
    setUser(
      ['id', 'username', 'name', 'first_name', 'last_name', 'email', 'avatar'].reduce(
        (usr, key) => ({
          ...usr,
          [key]: payload[key],
        }),
        {},
      ),
    )
  }

  return {
    payload: AUTH_USER,
    type: AUTH_FETCH_FULFILLED,
  }
}

export const authFetchCanceled = () => ({
  payload: null,
  type: AUTH_FETCH_CANCELED,
})

export const authFetchError = (payload) => {
  const credentials = storage.get('swa.credentials')
  if (credentials.rt) {
    // refresh token
    return authRefreshToken(credentials.rt)
  }
  storage.set('swa.user', null)

  return {
    payload,
    type: AUTH_FETCH_ERROR,
  }
}

export const authResetPassword = (email, useCodeKey) => ({
  payload: {
    email,
    useCodeKey,
  },
  type: AUTH_RESET_PASSWORD,
})

export const authResetPasswordFulfilled = (payload) => {
  trackEvent('Authentication', 'Password Reset', 'Done')
  return {
    payload,
    type: AUTH_RESET_PASSWORD_FULFILLED,
  }
}

export const authResetPasswordCanceled = () => ({
  payload: null,
  type: AUTH_RESET_PASSWORD_CANCELED,
})

export const authResetPasswordError = (payload) => ({
  payload,
  type: AUTH_RESET_PASSWORD_ERROR,
})

export const authChangePassword = (password, token) => ({
  payload: {
    password,
    token,
  },
  type: AUTH_CHANGE_PASSWORD,
})

export const authChangePasswordFulfilled = (payload) => {
  trackEvent('Authentication', 'Password Change', 'Done')
  return {
    payload,
    type: AUTH_CHANGE_PASSWORD_FULFILLED,
  }
}

export const authChangePasswordCanceled = () => ({
  payload: null,
  type: AUTH_CHANGE_PASSWORD_CANCELED,
})

export const authChangePasswordError = (payload) => ({
  payload,
  type: AUTH_CHANGE_PASSWORD_ERROR,
})

export const authLogout = () => ({
  payload: null,
  type: AUTH_LOGOUT,
})

export const authLogoutFulfilled = () => {
  trackEvent('Authentication', 'Logout', 'Done')
  if (document.location.pathname.endsWith('/settings')) {
    setTimeout(() => {
      document.location.href = '/'
    }, 200)
  } else if (document.location.pathname.indexOf('/auth/') === -1) {
    setTimeout(() => document.location.reload(), 200)
  }

  return {
    payload: null,
    type: AUTH_LOGOUT_FULFILLED,
  }
}

export const authLogoutError = (payload) => ({
  payload,
  type: AUTH_LOGOUT_ERROR,
})

export const authLoginByCredentialsEpic = epicRequest({
  actionType: [AUTH_LOGIN_CREDENTIALS, AUTH_LOGIN_NETWORK],
  debounce: 1000, // Wait 1s before the next request
  errorAction: authLoginError,
  fulfilledAction: authLoginFulfilled,
  maps: [authFetch],
  request: (action) => ({
    body: action.payload,
    method: 'post',
    url: `${URL_OAUTH_API}/token`,
  }),
  untilTypes: AUTH_LOGIN_CANCELED,
})

export const authRefreshTokenEpic = epicRequest({
  actionType: AUTH_REFRESH_TOKEN,
  errorAction: authRefreshTokenError,
  fulfilledAction: authRefreshTokenFulfilled,
  maps: [authFetch],
  request: (action) => ({
    body: action.payload,
    method: 'post',
    url: `${System.env.api.host}/oauth/token`,
  }),
  untilTypes: AUTH_REFRESH_TOKEN_CANCELED,
})

export const authResetPasswordEpic = epicRequest({
  actionType: AUTH_RESET_PASSWORD,
  debounce: 1000, // Wait 1s before the next request
  errorAction: authResetPasswordError,
  fulfilledAction: authResetPasswordFulfilled,
  maps: [
    (_, action) =>
      systemMessageShow({
        ...SYSTEM_MESSAGE_INITIAL_STATE,
        msg: <VerifyPasswordResetCode data={action.payload} />,
        options: [],
        title: 'Nueva contraseña',
      }),
  ],
  request: ({ payload }) => ({
    body: payload,
    method: 'post',
    url: 'auth/password/reset',
  }),
  untilTypes: AUTH_RESET_PASSWORD_CANCELED,
})

export const authChangePasswordEpic = epicRequest({
  actionType: AUTH_CHANGE_PASSWORD,
  debounce: 1000, // Wait 1s before the next request
  errorAction: authChangePasswordError,
  fulfilledAction: authChangePasswordFulfilled,
  maps: [
    () =>
      systemMessageShow({
        ...SYSTEM_MESSAGE_INITIAL_STATE,
        msg: 'Ya puedes ingresar con tu nueva contraseña.',
        options: [
          {
            ...SYSTEM_MESSAGE_ACTION,
            content: 'OK',
            onClick: () => {
              store.dispatch(authAction('login'))
              store.dispatch(systemMessageCanceled())
              document.location.href = '/'
            },
            primary: true,
          },
        ],
        title: '¡Cambio de contraseña!',
      }),
  ],
  request: ({ payload }) => ({
    body: payload,
    method: 'put',
    url: 'auth/password/reset',
  }),
  untilTypes: [AUTH_CHANGE_PASSWORD_CANCELED, AUTH_LOGIN_CANCELED],
})

export const authFetchEpic = epicRequest({
  actionType: AUTH_FETCH,
  errorAction: authFetchError,
  fulfilledAction: (response) => {
    const credentials = storage.get('swa.credentials')
    if (!credentials || !credentials.at) {
      return authFetchCanceled()
    }

    if (credentials.ex && credentials.ex - Date.now() <= 0) {
      return authRefreshToken(credentials.rt)
    }

    return authFetchFulfilled(response)
  },
  request: () => {
    const credentials = storage.get('swa.credentials')
    if (!credentials || !credentials.at || (credentials.ex && credentials.ex - Date.now() <= 0)) {
      // unable to ask for user
      return null
    }

    Object.assign(AUTH_HEADERS, {
      Accept: 'application/json',
      Authorization: `${credentials.tt} ${credentials.at}`,
    })
    System.AUTH_HEADERS = AUTH_HEADERS

    // request /me
    return 'me'
  },
  untilTypes: AUTH_FETCH_CANCELED,
})

export const authLogoutEpic = epicRequest({
  actionType: AUTH_LOGOUT,
  errorAction: authLogoutError,
  fulfilledAction: authLogoutFulfilled,
  request: () => {
    storage.clear()

    document.body.classList.remove('userIn')

    Object.keys(AUTH_HEADERS).forEach((key) => {
      delete AUTH_HEADERS[key]
    })
    System.AUTH_HEADERS = AUTH_HEADERS

    Object.keys(AUTH_USER).forEach((key) => {
      delete AUTH_USER[key]
    })

    return null
  },
  untilTypes: AUTH_LOGOUT,
})
